import type {
	TypedDocumentNode,
	ResultOf,
	VariablesOf,
} from "@graphql-typed-document-node/core";
import { analyzeDocument } from "graphql-request";
import { ErrorADT, safe } from "pb.safe";
import { v4 as uuidv4 } from "uuid";

import { logger } from "../shared/logger";
import * as Messages from "./client/messages";

export type Client = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	subscribe: <TQuery extends TypedDocumentNode<any, any>>(
		options: Client.subscribe.Options<TQuery>,
	) => Promise<
		| Client.Subscription<TQuery>
		| ErrorADT<
				/**
				 * Authentication token empty/invalid.
				 */
				| "TokenFailure"
				/**
				 * Took too long to establish successful data query subscription.
				 */
				| "InitTimeout"
				/**
				 * Failed to create valid data query subscription.
				 */
				| "InitFailure"
		  >
	>;
};

export namespace Client {
	export type Options = {
		/*
		 * Includes protocol part.
		 * e.g. `ws://localhost:3000`
		 */
		origin: string;
		getToken?: () => Promise<string | undefined>;
	};

	export type Subscription<TQuery> = {
		close: () => void;
		/**
		 * Called when the websocket recieves subscription query data.
		 */
		onData?: (
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			data: TQuery extends TypedDocumentNode<any, any>
				? ResultOf<TQuery>
				: TQuery,
		) => void;
		/**
		 * Called when the websocket closes unexpectedly.
		 */
		onDisconnect?: () => void;
	};

	export namespace subscribe {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		export type Options<TQuery extends TypedDocumentNode<any, any>> = {
			query: TQuery;
		} & (VariablesOf<TQuery> extends never
			? { variables?: never }
			: { variables: VariablesOf<TQuery> });
	}
}

export function Client({ origin, getToken }: Client.Options): Client {
	const host = origin.split("//")[1] ?? "";
	const href = `${origin}/graphql/realtime`;

	const subscribe: Client["subscribe"] = async ({ query, variables }) => {
		const subscription: Client.Subscription<unknown> = {
			/* v8 ignore next -- placeholder close reassigned before subscription is returned */
			close: () => undefined,
		};

		const token = await getToken?.();
		if (!token) {
			return ErrorADT("TokenFailure");
		}

		const authorization = { host, Authorization: token };
		const header = btoa(JSON.stringify(authorization));
		const payload = "e30="; // btoa("{}")
		const protocol = "graphql-ws";
		const socket = new WebSocket(
			`${href}?header=${header}&payload=${payload}`,
			protocol,
		);

		const handlers: {
			onOpen?: () => void;
			onClose?: (event: CloseEvent) => void;
			onError?: (event: Event) => void;
			onMessage?: (event: MessageEvent) => void;
		} = {};

		const onOpen = () => void handlers.onOpen?.();
		const onClose = (event: CloseEvent) => void handlers.onClose?.(event);
		const onError = (event: Event) => void handlers.onError?.(event);
		const onMessage = (event: MessageEvent) => void handlers.onMessage?.(event);

		socket.addEventListener("open", onOpen);
		socket.addEventListener("close", onClose);
		socket.addEventListener("error", onError);
		socket.addEventListener("message", onMessage);

		function dispose() {
			socket.removeEventListener("open", onOpen);
			socket.removeEventListener("message", onMessage);
			socket.removeEventListener("error", onError);
			socket.removeEventListener("close", onClose);
			socket.close(1000 /* Normal Closure */);
		}

		subscription.close = () => {
			dispose();
		};

		handlers.onClose = (event) => {
			if (event.code !== 1000 /* Normal Closure */) {
				logger.error("appsync ws: close abnormal", {
					code: event.code,
					reason: event.reason,
					wasClean: event.wasClean,
				});
			}

			dispose();

			subscription.onDisconnect?.();
		};

		handlers.onError = () => {
			logger.error("appsync ws: error");
			dispose();
			subscription.onDisconnect?.();
		};

		type ReturnValue = Awaited<ReturnType<typeof subscribe>>;
		let $resolve: ((value: ReturnValue) => void) | undefined = undefined;
		let $ack_timeout: ReturnType<typeof setTimeout> | undefined = undefined;
		const promise = new Promise<ReturnValue>(
			(resolve) => void ($resolve = resolve),
		);

		handlers.onOpen = () => {
			_onOpen();
		};

		function _onOpen() {
			// Ensure we fail with timeout error if the appsync protocol exchange
			// takes too long indicating potential failure.
			$ack_timeout =
				(void clearTimeout($ack_timeout),
				setTimeout(() => {
					// Close and cleanup. It's over.
					dispose();

					// Reuse timeout reference as lock marker.
					$ack_timeout = undefined;
					// Resume control flow to caller with error.
					$resolve?.(ErrorADT("InitTimeout"));

					logger.error("appsync ws: init timeout");
				}, 10_000 /* 10s */));

			handlers.onMessage = (event) => {
				const message = safe(() =>
					Messages.AppSyncConnectionAck.parse(JSON.parse(event.data)),
				);
				if (message instanceof Error) {
					return;
				}
				_onAcknowledge();
			};
			socket.send(
				JSON.stringify({
					type: "connection_init",
				} satisfies Messages.AppSyncConnectionInit),
			);
		}

		function _onAcknowledge() {
			handlers.onMessage = (event) => {
				{
					// Ok.
					const message = safe(() =>
						Messages.AppSyncStartAck.parse(JSON.parse(event.data)),
					);
					if (!(message instanceof Error)) {
						// If timeout hasn't fired yet.
						if ($ack_timeout) {
							// Cancel and clear timeout.
							$ack_timeout = void clearTimeout($ack_timeout);
							// Resume control flow to caller with successful subscription.
							$resolve?.(subscription);
						}

						return void _onSubscribe();
					}
				}
				{
					// Error.
					const message = safe(() =>
						Messages.AppSyncStartError.parse(JSON.parse(event.data)),
					);
					if (!(message instanceof Error)) {
						// Close and cleanup. It's over.
						dispose();

						// Cancel and clear timeout.
						$ack_timeout = void clearTimeout($ack_timeout);
						// Resume control flow to caller with error.
						const error = Object.assign(ErrorADT("InitFailure"), {
							message: message.payload.errors[0]?.message,
						});
						$resolve?.(error);

						logger.error("appsync ws: init failure", {
							errorName: error.name,
							errorMessage: error.message,
							errorStack: error.stack,
						});
					}
				}
			};
			socket.send(
				JSON.stringify({
					id: uuidv4(),
					type: "start",
					payload: {
						data: JSON.stringify({
							query: analyzeDocument(query).expression,
							variables,
						}),
						extensions: {
							authorization,
						},
					},
				} satisfies Messages.AppSyncStart),
			);
		}

		function _onSubscribe() {
			handlers.onMessage = (event) => {
				const message = safe(() =>
					Messages.AppSyncData.parse(JSON.parse(event.data)),
				);
				if (!(message instanceof Error)) {
					subscription.onData?.(message.payload.data);
				}
			};
		}

		return promise;
	};

	const client: Client = {
		subscribe,
	};

	return client;
}
